import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { Plus, Edit, Trash2, Scan, Users, UploadCloud, DownloadCloud, Barcode } from 'lucide-react';
import BarcodeScanner from '../components/BarcodeScanner';
import { toast } from 'sonner';
import Swal from 'sweetalert2';

import API from '../api_config';

function formatCLP(amount) {
  return new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    minimumFractionDigits: 0
  }).format(amount);
}

export default function ProductsPage() {
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    sku: '',
    description: '',
    category: '',
    price: '',
    cost: '',
    barcode: '',
    image_url: '',
    min_stock: '10',
    supplier_id: ''
  });

  const [categories, setCategories] = useState([]);
  const [units, setUnits] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [openScanner, setOpenScanner] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const handleDownloadTemplate = () => {
    window.location.href = `${API}/products/csv/template`;
  };

  const handleUploadCSV = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setIsUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      // Intenta usar el token si existe (axios interceptor lo suele enviar, pero lo ponemos manual para multiparts si es necesario)
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const headers = { 'Content-Type': 'multipart/form-data' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const res = await axios.post(`${API}/products/csv/upload`, formData, { headers });
      toast.success(res.data.message || 'Carga masiva completada');
      fetchProducts();
      fetchMetadata(); // Para actualizar categorías si se crearon nuevas
    } catch (error) {
      console.error(error);
      const msg = error.response?.data?.detail || 'Error al procesar el archivo CSV';
      toast.error(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setIsUploading(false);
      e.target.value = null; // resetea el input file
    }
  };

  const handleScan = (code) => {
    setFormData(prev => ({ ...prev, barcode: code }));
    setOpenScanner(false);
    toast.success('Código escaneado');
  };

  const generateBarcode = () => {
    // Genera un código de barras interno tipo EAN-13 (prefijo 200 reservado para uso interno)
    const prefix = '200';
    const randomDigits = Math.floor(Math.random() * 1000000000).toString().padStart(9, '0');
    const baseCode = prefix + randomDigits;
    
    let sum = 0;
    for (let i = 0; i < 12; i++) {
        sum += parseInt(baseCode[i]) * (i % 2 === 0 ? 1 : 3);
    }
    const checksum = (10 - (sum % 10)) % 10;
    const barcode = baseCode + checksum;
    
    setFormData(prev => ({ ...prev, barcode }));
    toast.success('Código de barras interno generado');
  };

  const fetchMetadata = useCallback(async () => {
    try {
        const [catRes, unitRes, suppRes] = await Promise.all([
            axios.get(`${API}/categories`),
            axios.get(`${API}/units`),
            axios.get(`${API}/suppliers`)
        ]);
        setCategories(catRes.data);
        setUnits(unitRes.data);
        setSuppliers(suppRes.data);
    } catch (e) {
        console.error("Error fetching metadata", e);
    }
  }, []);

  const fetchProducts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      toast.error('Error al cargar productos');
    }
  }, []);

  const searchProducts = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('query', searchQuery);
      if (categoryFilter) params.append('category', categoryFilter);
      if (minPrice) params.append('min_price', minPrice);
      if (maxPrice) params.append('max_price', maxPrice);

      const response = await axios.get(`${API}/search/products?${params.toString()}`);
      setProducts(response.data);
    } catch (error) {
      toast.error('Error al buscar productos');
    }
  }, [searchQuery, categoryFilter, minPrice, maxPrice]);

  useEffect(() => {
    fetchMetadata();
    fetchProducts();
  }, [fetchMetadata, fetchProducts]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (searchQuery || categoryFilter || minPrice || maxPrice) {
        searchProducts();
      } else {
        fetchProducts(); // Fallback if filters cleared
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchQuery, categoryFilter, minPrice, maxPrice, searchProducts, fetchProducts]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let finalSupplierId = formData.supplier_id;

      // Handle Manual Supplier Creation
      if (formData.supplier_id === 'manual_entry_placeholder') {
          try {
              toast.info('Creando proveedor...');
              const supRes = await axios.post(`${API}/suppliers`, {
                  name: 'Proveedor Manual Global',
                  rut: '99.999.999-9',
                  address: 'Generado desde Productos'
              });
              finalSupplierId = supRes.data.id;
              setSuppliers([...suppliers, supRes.data]);
          } catch (err) {
              console.error(err);
              toast.error('Error al crear proveedor manual');
              return;
          }
      }

      const payload = {
        ...formData,
        supplier_id: finalSupplierId,
        price: parseFloat(formData.price),
        cost: parseFloat(formData.cost),
        min_stock: parseInt(formData.min_stock)
      };

      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, payload);
        toast.success('Producto actualizado');
      } else {
        await axios.post(`${API}/products`, payload);
        toast.success('Producto creado');
      }

      setOpen(false);
      resetForm();
      fetchProducts();
    } catch (error) {
      console.error(error);
      toast.error('Error al guardar producto');
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      sku: product.sku,
      description: product.description || '',
      category: product.category,
      unit: product.unit || 'un',
      price: product.price.toString(),
      cost: product.cost.toString(),
      barcode: product.barcode || '',
      image_url: product.image_url || '',
      min_stock: product.min_stock.toString(),
      supplier_id: product.supplier_id || ''
    });
    setOpen(true);
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: '¿Estás seguro?',
      text: "No podrás revertir esta acción",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    });

    if (result.isConfirmed) {
      try {
        await axios.delete(`${API}/products/${id}`);
        Swal.fire(
          '¡Eliminado!',
          'El producto ha sido eliminado.',
          'success'
        );
        fetchProducts();
      } catch (error) {
        Swal.fire(
          'Error',
          'No se pudo eliminar el producto.',
          'error'
        );
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      sku: '',
      description: '',
      category: '',
      unit: 'un', // Default
      price: '',
      cost: '',
      barcode: '',
      image_url: '',
      min_stock: '10',
      supplier_id: ''
    });
    setEditingProduct(null);
  };

  const getSupplierName = (id) => {
      const s = suppliers.find(su => su.id === id);
      return s ? s.name : null;
  };

  return (
    <div className="space-y-6" data-testid="products-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight">Productos</h1>
          <p className="text-muted-foreground mt-2">Gestiona tu catálogo de productos</p>
        </div>
        <div className="flex gap-2 items-center">
            <Button variant="outline" onClick={handleDownloadTemplate} title="Descargar Plantilla CSV" className="rounded-full shadow-sm">
              <DownloadCloud className="h-4 w-4 md:mr-2" />
              <span className="hidden md:inline">Plantilla</span>
            </Button>
            
            <div className="relative">
                <input 
                    type="file" 
                    accept=".csv" 
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" 
                    onChange={handleUploadCSV}
                    disabled={isUploading}
                    title="Cargar archivo CSV de productos"
                />
                <Button variant="outline" disabled={isUploading} className="rounded-full shadow-sm">
                  <UploadCloud className="h-4 w-4 md:mr-2" />
                  <span className="hidden md:inline">{isUploading ? 'Subiendo...' : 'Importar CSV'}</span>
                </Button>
            </div>

            <Dialog open={open} onOpenChange={(val) => { setOpen(val); if (!val) resetForm(); }}>
              <DialogTrigger asChild>
                <Button className="rounded-full shadow-lg" data-testid="add-product-btn">
                  <Plus className="h-5 w-5 mr-2" />
                  <span className="hidden sm:inline">Nuevo Producto</span>
                </Button>
              </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingProduct ? 'Editar Producto' : 'Nuevo Producto'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nombre *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    data-testid="product-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>SKU *</Label>
                  <Input
                    value={formData.sku}
                    onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                    required
                    data-testid="product-sku-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Descripción</Label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  data-testid="product-description-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Categoría *</Label>
                  <select
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    required
                  >
                    <option value="">Selecciona categoría...</option>
                    {categories.map(c => (
                        <option key={c.id} value={c.name}>{c.name}</option>
                    ))}
                  </select>
                </div>
                 <div className="space-y-2">
                  <Label>Unidad *</Label>
                  <select
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={formData.unit}
                    onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                    required
                  >
                    <option value="">Selecciona unidad...</option>
                    {units.map(u => (
                        <option key={u.id} value={u.symbol}>{u.name} ({u.symbol})</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Proveedor Principal</Label>
                <select
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={formData.supplier_id}
                    onChange={(e) => setFormData({ ...formData, supplier_id: e.target.value })}
                >
                    <option value="">Sin proveedor asignado</option>
                    {suppliers.length === 0 ? (
                      <option value="manual_entry_placeholder" className="text-amber-600 font-semibold">
                          Inventario manual no existe (Crear Automático)
                      </option>
                    ) : (
                        suppliers.map(s => (
                            <option key={s.id} value={s.id}>{s.name}</option>
                        ))
                    )}
                </select>
              </div>

              <div className="space-y-2">
                 <Label>Código de Barras</Label>
                 <div className="flex gap-2">
                     <Input
                         value={formData.barcode}
                         onChange={(e) => setFormData({ ...formData, barcode: e.target.value })}
                         placeholder="Ingresa o genera código..."
                     />
                     <Button type="button" variant="outline" size="icon" onClick={() => setOpenScanner(true)} title="Escanear">
                         <Scan className="h-4 w-4" />
                     </Button>
                     <Button type="button" variant="secondary" onClick={generateBarcode} title="Generar código interno">
                         <Barcode className="h-4 w-4 mr-2" />
                         Generar
                     </Button>
                 </div>
              </div>

            <Dialog open={openScanner} onOpenChange={setOpenScanner}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Escanear Código</DialogTitle>
                        <DialogDescription>Apunta la cámara al código de barras.</DialogDescription>
                    </DialogHeader>
                    <BarcodeScanner onScan={handleScan} onClose={() => setOpenScanner(false)} />
                </DialogContent>
            </Dialog>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Precio Venta (CLP) *</Label>
                  <Input
                    type="number"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    required
                    data-testid="product-price-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Costo (CLP) *</Label>
                  <Input
                    type="number"
                    value={formData.cost}
                    onChange={(e) => setFormData({ ...formData, cost: e.target.value })}
                    required
                    data-testid="product-cost-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Stock Mínimo *</Label>
                  <Input
                    type="number"
                    value={formData.min_stock}
                    onChange={(e) => setFormData({ ...formData, min_stock: e.target.value })}
                    required
                    data-testid="product-minstock-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>URL de Imagen</Label>
                <Input
                  value={formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                  placeholder="https://..."
                  data-testid="product-image-input"
                />
              </div>

              <div className="flex justify-end space-x-2">
                <Button type="button" variant="outline" onClick={() => { setOpen(false); resetForm(); }}>
                  Cancelar
                </Button>
                <Button type="submit" data-testid="save-product-btn">
                  {editingProduct ? 'Actualizar' : 'Crear'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="md:col-span-2">
              <Label className="text-sm mb-2">Buscar</Label>
              <Input
                placeholder="Nombre, SKU o descripción..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="search-products-input"
              />
            </div>
            <div>
              <Label className="text-sm mb-2">Categoría</Label>
              <Input
                placeholder="Filtrar por categoría"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                data-testid="category-filter-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-sm mb-2">Precio Mín.</Label>
                <Input
                  type="number"
                  placeholder="Min"
                  value={minPrice}
                  onChange={(e) => setMinPrice(e.target.value)}
                  data-testid="min-price-input"
                />
              </div>
              <div>
                <Label className="text-sm mb-2">Precio Máx.</Label>
                <Input
                  type="number"
                  placeholder="Max"
                  value={maxPrice}
                  onChange={(e) => setMaxPrice(e.target.value)}
                  data-testid="max-price-input"
                />
              </div>
            </div>
          </div>
          {(searchQuery || categoryFilter || minPrice || maxPrice) && (
            <div className="mt-3 flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                {products.length} producto(s) encontrado(s)
              </p>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSearchQuery('');
                  setCategoryFilter('');
                  setMinPrice('');
                  setMaxPrice('');
                }}
              >
                Limpiar filtros
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {products.length === 0 ? (
          <Card className="col-span-full">
            <CardContent className="text-center py-12 text-muted-foreground">
              No hay productos registrados
            </CardContent>
          </Card>
        ) : (
          products.map(product => (
            <Card key={product.id} className="hover:shadow-lg transition-shadow" data-testid={`product-${product.id}`}>
              <CardHeader>
                {product.image_url && (
                  <img
                    src={product.image_url}
                    alt={product.name}
                    className="w-full h-48 object-cover rounded-lg mb-4"
                  />
                )}
                <div className="flex justify-between items-start">
                    <CardTitle className="text-lg">{product.name}</CardTitle>
                </div>
                <p className="text-sm text-muted-foreground font-mono">{product.sku}</p>
                {product.supplier_id && getSupplierName(product.supplier_id) && (
                    <div className="flex items-center text-xs text-indigo-600 mt-1">
                        <Users className="h-3 w-3 mr-1" />
                        {getSupplierName(product.supplier_id)}
                    </div>
                )}
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Precio:</span>
                  <span className="font-mono font-bold text-lg">{formatCLP(product.price)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Costo:</span>
                  <span className="font-mono font-medium">{formatCLP(product.cost)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Categoría:</span>
                  <span className="text-sm capitalize">{product.category}</span>
                </div>
                <div className="flex space-x-2 pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEdit(product)}
                    className="flex-1"
                    data-testid={`edit-product-${product.id}`}
                  >
                    <Edit className="h-4 w-4 mr-1" />
                    Editar
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => handleDelete(product.id)}
                    data-testid={`delete-product-${product.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}